import { ChevronDown } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'

import { StatusPill } from '@/shared/components/common'
import { Popover } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import type { Stage } from '@/shared/types/domain'

export interface StagePickerProps {
  stage: Stage
  stages: readonly Stage[]
  onChange: (stage: Stage) => void
  disabled?: boolean
  /** `sm` para filas densas (la de una tarea); `md` para la barra de un proyecto. */
  size?: 'sm' | 'md'
  /** Textos de la ayuda y de la lista. Default: los de la fase de un proyecto. */
  help?: string
  listLabel?: string
  className?: string
}

/**
 * La fase como pill editable de UN CLICK.
 *
 * Mover un proyecto de fase es la accion mas frecuente de esta pantalla. Si
 * costara abrir un formulario, la fase dejaria de estar al dia y el tablero
 * pasaria a mentir.
 */
export function StagePicker({
  stage,
  stages,
  onChange,
  disabled,
  size = 'md',
  help,
  listLabel,
  className,
}: StagePickerProps) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])

  return (
    // El NOMBRE de la fase lo pone cada empresa en su stage set: no se traduce.
    // Lo nuestro es el andamiaje — el `aria-label` de la lista y la ayuda.
    <div
      ref={container}
      title={disabled ? undefined : (help ?? t('projects.stage.help'))}
      className={className}
    >
      <StatusPill
        size={size}
        category={stage.category}
        onClick={disabled ? undefined : () => setOpen((value) => !value)}
        /*
          El chevron es la única señal de que esto se puede tocar.

          Sin él, el pill de la fase es idéntico al de cualquier otro estado de
          la app —que no hacen nada— y mover un proyecto a "Pausado" quedaba
          descubierto por accidente: es la acción más frecuente de la pantalla y
          se reportó como faltante estando hecha.
        */
        trailing={
          disabled ? null : (
            <ChevronDown size={12} aria-hidden="true" className="-mr-0.5 shrink-0 opacity-70" />
          )
        }
      >
        {stage.name}
      </StatusPill>

      <Popover anchor={container} open={open} onClose={close} className="w-[180px] p-1">
        <ul
          role="listbox"
          aria-label={listLabel ?? t('projects.stage.change')}
          className="flex flex-col"
        >
          {stages.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                role="option"
                aria-selected={option.id === stage.id}
                onClick={() => {
                  setOpen(false)
                  if (option.id !== stage.id) onChange(option)
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-[var(--radius-control)] px-1.5 py-1 text-left',
                  'transition-colors duration-[var(--motion-fast)] hover:bg-surface-2',
                )}
              >
                <StatusPill category={option.category}>{option.name}</StatusPill>
              </button>
            </li>
          ))}
        </ul>
      </Popover>
    </div>
  )
}
